import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useWindowDimensions } from 'react-native'

import styled from 'styled-components/native'

import { ActivityStatus, ClientFiltersActivityStatusSection } from './sections/ClientFiltersActivityStatusSection'
import { ClientFiltersEpisodeProgressSection, EpisodeProgress } from './sections/ClientFiltersEpisodeProgressSection'
import { AiCoachStatus, ClientFiltersLyraAiSection } from './sections/ClientFiltersLyraAiSection'
import { ClientFilterMeetingFormats, ClientFiltersModalitySection } from './sections/ClientFiltersModalitySection'
import { ClientFiltersNextSessionSection, NextSession } from './sections/ClientFiltersNextSessionSection'
import { ClientFiltersProgramSection } from './sections/ClientFiltersProgramSection'
import { ClientFiltersSessionTypeSection, SessionType } from './sections/ClientFiltersSessionTypeSection'
import { ClientFiltersStatusSection, ClientFilterStatus } from './sections/ClientFiltersStatusSection'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { TertiaryButton } from '../../atoms/tertiaryButton/TertiaryButton'
import { tID } from '../../utils/utils'

export enum ClientFilterType {
  PROGRAMS = 'PROGRAMS',
  EPISODE_PROGRESS = 'EPISODE_PROGRESS',
  LYRA_AI = 'LYRA_AI',
}

export type ClientFiltersState = {
  status: ClientFilterStatus
  aiCoachStatus: AiCoachStatus[]
  programs: string[]
  nextSession: NextSession[]
  sessionType: SessionType[]
  episodeProgress?: EpisodeProgress[]
  activityStatus?: ActivityStatus[]
  modality?: ClientFilterMeetingFormats[]
}

export { ClientFilterStatus, EpisodeProgress, NextSession, SessionType, ActivityStatus }

export const INITIAL_FILTER_STATE: ClientFiltersState = {
  status: ClientFilterStatus.ACTIVE,
  aiCoachStatus: [],
  programs: [],
  nextSession: [],
  sessionType: [],
  episodeProgress: [],
  activityStatus: [],
  modality: [],
}

type ClientFiltersPopoverProps = {
  programs?: string[]
  filterState: ClientFiltersState
  onFilterStateChange: (filterState: ClientFiltersState) => void
  onClose: () => void
  availableFilters: ClientFilterType[]
  isEngageProvider?: boolean
}

const Container = styled.View(({ theme }) => ({
  border: `1px solid ${theme.colors.borderDefault}`,
  boxShadow: `0 4px 16px ${theme.colors.shadowLow}`,
  width: '320px',
  borderRadius: '16px',
  backgroundColor: theme.colors.backgroundPrimary,
}))
const SectionsContainer = styled.View<{ windowHeight: number }>(({ theme, windowHeight }) => ({
  paddingTop: theme.spacing['24px'],
  gap: theme.spacing['16px'],
  maxHeight: windowHeight >= 750 ? '504px' : '304px',
  overflow: 'auto',
  paddingLeft: theme.spacing['24px'],
  paddingRight: theme.spacing['24px'],
}))

const ButtonContainer = styled.View(({ theme }) => ({
  gap: theme.spacing['8px'],
  paddingLeft: theme.spacing['24px'],
  paddingRight: theme.spacing['24px'],
  boxShadow: `0 -4px 16px ${theme.colors.shadowLow}`,
  paddingTop: theme.spacing['16px'],
  paddingBottom: theme.spacing['24px'],
}))

export const ClientFiltersPopover: React.FC<ClientFiltersPopoverProps> = ({
  programs = [],
  filterState,
  onFilterStateChange,
  onClose,
  availableFilters,
  isEngageProvider,
}) => {
  const { height } = useWindowDimensions()
  const [dirtyState, setDirtyState] = useState(filterState)

  useEffect(() => {
    setDirtyState(filterState)
  }, [filterState])

  return (
    <Container>
      <SectionsContainer windowHeight={height}>
        <ClientFiltersStatusSection
          value={dirtyState.status}
          onChange={(value) => {
            setDirtyState((prev) => ({ ...prev, status: value }))
          }}
        />
        {availableFilters.includes(ClientFilterType.LYRA_AI) && (
          <ClientFiltersLyraAiSection
            values={dirtyState.aiCoachStatus}
            onChange={(values) => {
              setDirtyState((prev) => ({ ...prev, aiCoachStatus: values }))
            }}
          />
        )}
        {availableFilters.includes(ClientFilterType.PROGRAMS) && (
          <ClientFiltersProgramSection
            programs={programs}
            values={dirtyState.programs}
            onChange={(values) => {
              setDirtyState((prev) => ({ ...prev, programs: values }))
            }}
          />
        )}
        <ClientFiltersNextSessionSection
          values={dirtyState.nextSession}
          onChange={(values) => {
            setDirtyState((prev) => ({ ...prev, nextSession: values }))
          }}
        />
        <ClientFiltersSessionTypeSection
          values={dirtyState.sessionType}
          onChange={(values) => {
            setDirtyState((prev) => ({ ...prev, sessionType: values }))
          }}
        />
        {dirtyState.episodeProgress && availableFilters.includes(ClientFilterType.EPISODE_PROGRESS) && (
          <ClientFiltersEpisodeProgressSection
            values={dirtyState.episodeProgress}
            onChange={(values) => {
              setDirtyState((prev) => ({ ...prev, episodeProgress: values }))
            }}
          />
        )}
        {dirtyState.activityStatus && (
          <ClientFiltersActivityStatusSection
            values={dirtyState.activityStatus}
            onChange={(values) => {
              setDirtyState((prev) => ({ ...prev, activityStatus: values }))
            }}
          />
        )}
        {dirtyState.modality && (
          <ClientFiltersModalitySection
            values={dirtyState.modality}
            onChange={(values) => {
              setDirtyState((prev) => ({ ...prev, modality: values }))
            }}
            isEngageProvider={isEngageProvider}
          />
        )}
      </SectionsContainer>
      <ButtonContainer>
        <PrimaryButton
          testID={tID('ClientFilters-save-button')}
          fullWidth
          text={<FormattedMessage defaultMessage='Save' description='Button to save filters' />}
          onPress={() => {
            onClose()
            onFilterStateChange(dirtyState)
          }}
        />
        <TertiaryButton
          testID={tID('ClientFilters-reset-button')}
          fullWidth
          text={<FormattedMessage defaultMessage='Reset' description='Button to reset filters' />}
          onPress={() => {
            onClose()
            onFilterStateChange(INITIAL_FILTER_STATE)
          }}
        />
      </ButtonContainer>
    </Container>
  )
}
